import { GRID_SIZE, TILE_TYPES } from './constants/index';
import { logger } from './logger';
import GridIterator from '@utils/GridIterator';
import { isTileType } from '@utils/TileUtils';
import type { TileObject } from '@types/game';

interface ZoneGenContext {
    grid: (number | TileObject | null)[][];
    enemies?: Array<{ x: number; y: number; [key: string]: unknown }>;
    currentZoneX?: number | null;
    currentZoneY?: number | null;
    currentDimension?: number;
    game?: {
        portTransitionData?: { x: number; y: number; [key: string]: unknown };
        [key: string]: unknown;
    };
    [key: string]: unknown;
}

export function isTileFree(zoneGen: ZoneGenContext, x: number, y: number): boolean {
    const tile = zoneGen.grid[y][x];
    if (isTileType(tile, TILE_TYPES.WALL) || isTileType(tile, TILE_TYPES.ROCK) || isTileType(tile, TILE_TYPES.SHRUBBERY) || isTileType(tile, TILE_TYPES.HOUSE) || isTileType(tile, TILE_TYPES.DEADTREE) || isTileType(tile, TILE_TYPES.WELL) || isTileType(tile, TILE_TYPES.SHACK) || isTileType(tile, TILE_TYPES.SIGN)) return false;
    if (zoneGen.enemies && zoneGen.enemies.some(e => e.x === x && e.y === y)) return false;
    if (isTileType(tile, TILE_TYPES.AXE) || isTileType(tile, TILE_TYPES.HAMMER) || isTileType(tile, TILE_TYPES.BISHOP_SPEAR)) return false;
    return true;
}

export function findOpenNpcSpawn(zoneGen: ZoneGenContext, requiredNeighbors: number): { x: number; y: number } | null {
    const candidates = GridIterator.findTiles(
        zoneGen.grid,
        (tile: number | TileObject | null, x: number, y: number): boolean => {
            if (!isTileFree(zoneGen, x, y)) return false;

            let walkableNeighbors = 0;
            for (let dy = -1; dy <= 1; dy++) {
                for (let dx = -1; dx <= 1; dx++) {
                    if (dx === 0 && dy === 0) continue;
                    const nx = x + dx;
                    const ny = y + dy;
                    if (isTileFree(zoneGen, nx, ny)) {
                        walkableNeighbors++;
                    }
                }
            }
            return walkableNeighbors >= requiredNeighbors;
        },
        { skipBorders: true }
    );

    return candidates.length > 0 ? candidates[Math.floor(Math.random() * candidates.length)] : null;
}

export function findValidPlayerSpawn(zoneGen: ZoneGenContext, avoidEntrance = false): { x: number; y: number } | null {
    // Home zone spawn behavior - spawn on an exit tile for cinematic arrival
    if (zoneGen.currentZoneX === 0 && zoneGen.currentZoneY === 0 && zoneGen.currentDimension === 0) {
        // Find all exit tiles in the zone
        const exitTiles = GridIterator.findTiles(zoneGen.grid, (tile: number | TileObject | null) => isTileType(tile, TILE_TYPES.EXIT));

        // Spawn on a random exit tile if any exist
        if (exitTiles.length > 0) {
            const spawnPos = exitTiles[Math.floor(Math.random() * exitTiles.length)];
            logger.debug(`[SpawnManager] Spawning on EXIT tile at (${spawnPos.x},${spawnPos.y})`);
            return spawnPos;
        }

        // Fallback to original behavior if no exits found
        logger.warn('[SpawnManager] No EXIT tiles found, using fallback spawn logic');
        const houseStartX = 3;
        const houseStartY = 3;
        const frontY = houseStartY + 3;
        for (let x = houseStartX; x < houseStartX + 4; x++) {
            if (isTileFree(zoneGen, x, frontY)) {
                return { x, y: frontY };
            }
        }
    }

    const entrancePos = zoneGen.game?.portTransitionData;
    const candidates = GridIterator.findTiles(
        zoneGen.grid,
        (tile: number | TileObject | null, x: number, y: number): boolean => {
            if (avoidEntrance && entrancePos && x === entrancePos.x && y === entrancePos.y) return false;
            return isTileFree(zoneGen, x, y);
        },
        { skipBorders: true }
    );

    if (candidates.length > 0) return candidates[Math.floor(Math.random() * candidates.length)];
    return null;
}
